'use client';
import { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import { ScrollScrubVideo } from '../ScrollScrubVideo/ScrollScrubVideo';

interface UseLazyLoadOptions {
  threshold?: number;
  rootMargin?: string;
}

const useLazyLoad = (options: UseLazyLoadOptions = {}) => {
  const { threshold = 0.05, rootMargin = '100px' } = options;
  const [shouldLoad, setShouldLoad] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Set up timer to load after 5 seconds if above viewport (reduced from 10s for better UX)
    // This handles the case where video is above viewport on navigation back
    const checkIfAboveViewport = () => {
      const rect = element.getBoundingClientRect();
      return rect.bottom < 0;
    };

    const timer = setTimeout(
      () => {
        if (!shouldLoad) {
          setShouldLoad(true);
        }
      },
      checkIfAboveViewport() ? 5000 : 10000
    ); // Shorter timer if above viewport

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoad) {
            setShouldLoad(true);
            // Once we've decided to load, we can disconnect the observer and clear timer
            observer.disconnect();
            clearTimeout(timer);
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [threshold, rootMargin, shouldLoad]);

  return { elementRef, shouldLoad };
};

export const BenifitVideo = () => {
  const { elementRef, shouldLoad } = useLazyLoad({
    threshold: 0.05,
    rootMargin: '1000px', // Start loading when element is 1000px away from viewport
  });

  const renderFallback = () => {
    return (
      <div className="max-w-6xl px-4 mx-auto sm:px-6 min-h-[100vh] w-full relative">
        <Image
          src="/hero/global.png"
          alt="Global tone preview"
          fill
          className="object-cover"
          priority={shouldLoad} // Prioritize loading if we're about to show the video
        />
      </div>
    );
  };

  return (
    <div ref={elementRef} className={`min-h-[200vh] sm:min-h-[400vh] w-full`}>
      {!shouldLoad ? (
        // Show image fallback before component enters viewport
        renderFallback()
      ) : (
        // Render the actual video component
        <ScrollScrubVideo
          src="/hero/global-g-5.mp4"
          firefoxSrc="/hero/global-g-1.mp4"
          mobileSrc="/hero/global-g-1-mobile.mp4"
          posterSrc="/hero/global.png"
          minWidth={0}
          scrubPages={4}
          scrubPagesMobile={2}
          zoomDuration={0.2}
          loadFadeDuration={0.5}
        />
      )}
    </div>
  );
};
